import React, { useEffect, useMemo, useState } from "react";
import { AdminCompanyExtended } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  Empty,
  Panel,
  Table,
  SortConfig,
  SortDirection,
} from "../AdminUI";

interface Props {
  onSelectCompany: (c: AdminCompanyExtended) => void;
  cached: AdminCompanyExtended[] | null;
  onCache: (data: AdminCompanyExtended[]) => void;
  refreshKey: number;
}

export default function CompaniesList({
  onSelectCompany,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const PAGE_SIZE_OPTIONS = [25, 50, 100];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<AdminCompanyExtended[]>(
    cached || []
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";

    // If clicking the same header, toggle direction
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ key, direction });
    setPage(1);
  };

  const orderBy = useMemo(() => {
    if (!sortConfig?.key || !sortConfig?.direction) return "";

    const sortKeyMap: Record<string, string> = {
      contact_name: "first_name",
      plan: "plan",
    };
    const key = sortKeyMap[sortConfig.key] || sortConfig.key;
    return sortConfig.direction === "desc" ? `-${key}` : key;
  }, [sortConfig]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await adminService.fetchCompanies({
          page,
          size: pageSize,
          search: debouncedSearch,
          orderBy,
        });
        if (!mounted) return;
        setCompanies(result.rows || []);
        setTotal(result.total || 0);
        setHasNext(result.hasNext);
        setHasPrevious(result.hasPrevious);
        onCache(result.rows || []);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [refreshKey, page, pageSize, debouncedSearch, orderBy]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const cols: Column<AdminCompanyExtended>[] = [
    {
      key: "name",
      header: "Empresa",
      render: (r) => <span className="font-medium">{r.name}</span>,
      sortable: true,
    },
    {
      key: "sector",
      header: "Sector",
      sortable: true,
      render: (r) => r.sector || "—",
    },
    {
      key: "contact_name",
      header: "Nombre de contacto",
      sortable: true,
      render: (r) => {
        const fullName = `${r.first_name || ""} ${r.last_name || ""}`.trim();
        return fullName || "—";
      },
    },
    {
      key: "phone",
      header: "Teléfono de contacto",
      sortable: true,
      render: (r) => r.phone || "—",
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      render: (r) => r.email || "—",
    },
    { key: "city", header: "Ciudad", sortable: true },
    { key: "country", header: "País", sortable: true },
    {
      key: "plan",
      header: "Plan",
      sortable: true,
      render: (r) => (r.plan && typeof r.plan === "object" ? r.plan.name : "—"),
    },
    {
      key: "created_at",
      header: "Creada",
      sortable: true,
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
    },
    {
      key: "referral_info",
      header: "Referido Por",
      sortable: true,
      render: (r) => {
          if (!r.referral_info) return "—";
          return (
              <div className="text-xs">
                  <div className="font-bold">{r.referral_info.sales_rep_name}</div>
                  <div className="text-slate-500">{r.referral_info.code}</div>
              </div>
          );
      }
    },
  ];

  return (
    <Panel
      title="Empresas"
      right={
        <div className="admin-list-controls">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar empresas..."
            className="input"
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="admin-page-size"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / pág.
              </option>
            ))}
          </select>
          <span className="admin-list-meta">{total} resultados</span>
        </div>
      }
    >
      {loading ? (
        <Empty>Cargando empresas…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : companies.length ? (
        <>
          <Table
            columns={cols}
            rows={companies}
            keyField="id"
            onRowClick={onSelectCompany}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
          <div className="admin-pagination">
            <button
              className="action-btn ghost"
              disabled={!hasPrevious || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span className="admin-pagination-info">
              Página {page} de {totalPages}
            </span>
            <button
              className="action-btn ghost"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </>
      ) : (
        <Empty>No se encontraron empresas.</Empty>
      )}
    </Panel>
  );
}
